import React, { useEffect, useState } from 'react';
import { CheckCircle, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/AuthContext';
import { paymentAPI } from '../utils/api';
import { formatCurrency } from '../lib/utils';

const loadRazorpay = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.async = true;
  script.onload = resolve;
  script.onerror = () => reject(new Error('Secure subscription checkout could not be loaded.'));
  document.head.appendChild(script);
});

export default function SubscriptionPlansPage() {
  const navigate = useNavigate();
  const { isSignedIn, user } = useUser();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    paymentAPI.getSubscriptionPlans({ signal: controller.signal }).then(response => setPlans(response.data.data || []))
      .catch(failure => { if (failure.code !== 'ERR_CANCELED') setError('Subscription plans could not be loaded.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, []);

  const subscribe = async plan => {
    if (!isSignedIn) { navigate('/sign-in'); return; }
    try {
      setBusy(plan.id); setError('');
      const response = await paymentAPI.createSubscription(plan.id);
      const subscription = response.data.data;
      await loadRazorpay();
      await new Promise((resolve, reject) => {
        const checkout = new window.Razorpay({
          key: subscription.checkout.key_id,
          subscription_id: subscription.checkout.subscription_id,
          name: 'iStockly',
          description: `${plan.bundle.name} — ${plan.name}`,
          prefill: { name: user?.name, email: user?.email },
          modal: { ondismiss: () => reject(new Error('Subscription checkout was closed. You can safely try again.')) },
          handler: () => { navigate('/account/commerce?subscription=pending'); resolve(); },
        });
        checkout.on('payment.failed', event => reject(new Error(event.error?.description || 'Subscription payment failed.')));
        checkout.open();
      });
    } catch (failure) {
      setError(failure.response?.data?.message || Object.values(failure.response?.data?.errors || {})[0]?.[0] || failure.message || 'Subscription could not be started.');
    } finally { setBusy(''); }
  };

  if (loading) return <main className="min-h-screen bg-black px-4 pt-32 text-center text-white" role="status">Loading subscription plans…</main>;

  return <main className="min-h-screen bg-black px-4 pb-20 pt-28 text-white">
    <div className="mx-auto max-w-6xl">
      <header className="mx-auto max-w-3xl text-center"><p className="font-semibold text-yellow-400">iStockly memberships</p><h1 className="mt-3 text-4xl font-bold">Learn across a complete course bundle</h1><p className="mt-4 text-gray-400">Choose an available billing interval. Access begins only after Razorpay confirms the subscription.</p></header>
      {error && <p role="alert" className="mx-auto mt-8 max-w-2xl rounded border border-red-600 bg-red-950/40 p-4 text-red-200">{error}</p>}
      {plans.length === 0 ? <p className="mt-12 text-center text-gray-400">No subscription plans are currently available.</p> : <section aria-label="Subscription plans" className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {plans.map(plan => <article key={plan.id} className="flex flex-col rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
          <p className="text-sm font-semibold uppercase tracking-wide text-yellow-400">{plan.bundle.name}</p><h2 className="mt-2 text-2xl font-bold">{plan.name}</h2>
          <p className="mt-4 text-3xl font-bold">{formatCurrency(plan.amount_minor / 100)} <span className="text-sm font-normal text-gray-400">/ {plan.interval_count > 1 ? `${plan.interval_count} ` : ''}{plan.interval.replace('ly', '')}</span></p>
          <p className="mt-2 text-xs text-gray-400">GST {plan.tax_mode === 'inclusive' ? 'included' : 'calculated by Razorpay as configured'}.</p>
          {plan.bundle.description && <p className="mt-5 text-sm text-gray-300">{plan.bundle.description}</p>}
          <ul className="mt-6 flex-1 space-y-3">{plan.bundle.courses.map(course => <li key={course.public_id} className="flex gap-2 text-sm text-gray-300"><CheckCircle className="h-5 w-5 shrink-0 text-green-400"/>{course.title}</li>)}</ul>
          <button disabled={busy === plan.id} onClick={() => subscribe(plan)} className="mt-8 rounded-lg bg-yellow-400 px-5 py-3 font-semibold text-black disabled:opacity-50">{busy === plan.id ? 'Opening checkout…' : isSignedIn ? 'Choose plan' : 'Sign in to subscribe'}</button>
        </article>)}
      </section>}
      <p className="mx-auto mt-10 flex max-w-xl items-center justify-center gap-2 text-center text-sm text-gray-400"><Shield className="h-5 w-5 text-green-400"/>Recurring payments and cancellations are processed using the server-authoritative Razorpay subscription.</p>
    </div>
  </main>;
}
